import React from 'react';
import { Spin, Input, Row, Col, Tag, Typography, Form, Progress } from 'antd';
import { Container } from '@/components/ui/Container';
import GlobalTable from '@/components/ui/table/Table';
import AppModal from '@/pages/inside/tenant/AppModal';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';

const formatLabel = (v) => (v == null || v === '' ? '-' : String(v));

function formatDateTime(value) {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return formatLabel(value);
    const date = new Intl.DateTimeFormat(undefined, { month: 'long', day: '2-digit', year: 'numeric' }).format(d);
    const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d);
    return `${date} | ${time}`;
}

function formatMoney(value) {
    if (value == null || value === '') return '-';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toneForStatus(status) {
    const s = String(status ?? '').toLowerCase();
    if (s === 'active') return 'green';
    if (s === 'pending_completion') return 'gold';
    if (s === 'completed' || s === 'done') return 'blue';
    if (s === 'cancelled' || s === 'canceled') return 'red';
    return 'default';
}

function toneForPriority(priority) {
    const p = String(priority ?? '').toLowerCase();
    if (p === 'high') return 'red';
    if (p === 'medium') return 'gold';
    if (p === 'low') return 'blue';
    return 'default';
}

export default function ClientDetailModal({ open, client, loading = false, onProjectClick, onClose }) {
    const projects = Array.isArray(client?.projects) ? client.projects : [];
    const { Text } = Typography;

    const columns = [
        {
            title: 'Project',
            dataIndex: 'name',
            key: 'name',
            render: (v) => <Text strong>{formatLabel(v)}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (v) => <Tag color={toneForStatus(v)}>{formatLabel(v)}</Tag>,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (v) => <Tag color={toneForPriority(v)}>{formatLabel(v)}</Tag>,
        },
        {
            title: 'Budget',
            dataIndex: 'budget',
            key: 'budget',
            render: (v) => formatMoney(v),
        },
        {
            title: 'Completion',
            key: 'completionPercent',
            render: (_, record) => {
                const pct = Math.max(0, Math.min(100, Number(record?.progress?.completionPercent ?? 0) || 0));
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                        <Progress percent={pct} size="small" showInfo={false} style={{ width: 120, margin: 0 }} />
                        <span style={{ minWidth: 42, fontWeight: 600 }}>{pct}%</span>
                    </div>
                );
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (v) => formatDateTime(v),
        },
    ];

    return (
        <AppModal
            size="lg"
            lgWidth="min(1180px, calc(100vw - 48px))"
            open={open}
            title="Client Details"
            onCancel={onClose}
            footer={null}
        >
            {loading ? (
                <Container $display="flex" $justifyContent="center" $alignItems="center" $padding="24px 0">
                    <Spin />
                </Container>
            ) : (
                <Container $display="flex" $flexDirection="column" $gap="16px">
                    <Container $display="flex" $flexDirection="column" $gap="10px">
                        <Form
                            layout="vertical"
                            requiredMark={false}
                            styles={projectSprintTaskModalFormStyles}
                        >
                            <Row gutter={[16, 8]}>
                                <Col xs={24} md={12}>
                                    <Form.Item label={<span style={submissionRichDescriptionLabelStyle}>Name</span>}>
                                        <Input size="large" value={formatLabel(client?.name)} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label={<span style={submissionRichDescriptionLabelStyle}>Type</span>}>
                                        <Input
                                            size="large"
                                            value={formatLabel(client?.typeLabel ?? client?.type)}
                                            readOnly
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label={<span style={submissionRichDescriptionLabelStyle}>Email</span>}>
                                        <Input size="large" value={formatLabel(client?.email)} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label={<span style={submissionRichDescriptionLabelStyle}>Phone</span>}>
                                        <Input size="large" value={formatLabel(client?.phone)} readOnly />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Container>

                    <Container $display="flex" $flexDirection="column" $gap="10px">
                        <Text style={{ fontWeight: 700, color: 'rgba(0,0,0,0.88)' }}>
                            Project History
                        </Text>
                        <GlobalTable
                            title=""
                            columns={columns}
                            dataSource={projects.map((p) => ({ ...p, key: p?.id }))}
                            showSearch={false}
                            showFilter={false}
                            showMore={false}
                            hideToolbar
                            pagination={false}
                            $topMargin={false}
                            $radius={12}
                            onRow={(record) => ({
                                onClick: () => onProjectClick?.(record?.id),
                                style: { cursor: record?.id ? 'pointer' : 'default' },
                            })}
                        />
                    </Container>
                </Container>
            )}
        </AppModal>
    );
}

